import { useState } from 'react'
import { createInvites, type InviteCreateResponse } from '../../services/adminApi'
import { CustomButton } from '../../components/common/CustomButton'
import { CustomTextInput } from '../../components/common/CustomTextInput'

interface Props {
  onClose: () => void
  onCreated: (result: InviteCreateResponse) => void
}

export function CreateInviteModal({ onClose, onCreated }: Props) {
  const [mode, setMode] = useState<'emails' | 'count'>('emails')
  const [emailsText, setEmailsText] = useState('')
  const [count, setCount] = useState(5)
  const [expiresInDays, setExpiresInDays] = useState(30)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleCreate = async () => {
    setSaving(true)
    setError(null)
    try {
      const emails = mode === 'emails'
        ? emailsText.split(/[\n,]/).map((e) => e.trim()).filter(Boolean)
        : []

      if (mode === 'emails' && emails.length === 0) {
        setError('Enter at least one email address')
        setSaving(false)
        return
      }

      const data = mode === 'emails'
        ? { emails, expires_in_days: expiresInDays }
        : { count, expires_in_days: expiresInDays }

      const response = await createInvites(data)
      if (response.ok && response.data) {
        onCreated(response.data)
      } else {
        setError(response.error?.message || 'Failed to create invites')
      }
    } catch {
      setError('Failed to connect to API')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white dark:bg-[#1a1520] p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-6">
          <h2 className="font-heading text-xl font-bold text-gray-900 dark:text-gray-100">Create Invites</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="mb-4 rounded-xl bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-700 dark:text-red-300">{error}</div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Invite type</label>
            <div className="flex gap-2">
              <button
                onClick={() => setMode('emails')}
                className={`rounded-xl px-4 py-2 text-sm font-medium transition-colors ${
                  mode === 'emails'
                    ? 'bg-[#503864] text-white'
                    : 'bg-gray-100 text-gray-700 dark:bg-[#2d2236] dark:text-gray-300'
                }`}
              >
                With Emails
              </button>
              <button
                onClick={() => setMode('count')}
                className={`rounded-xl px-4 py-2 text-sm font-medium transition-colors ${
                  mode === 'count'
                    ? 'bg-[#503864] text-white'
                    : 'bg-gray-100 text-gray-700 dark:bg-[#2d2236] dark:text-gray-300'
                }`}
              >
                Generic Links
              </button>
            </div>
          </div>

          {mode === 'emails' ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email addresses (one per line or comma-separated)
              </label>
              <textarea
                value={emailsText}
                onChange={(e) => setEmailsText(e.target.value)}
                rows={5}
                className="w-full rounded-xl border border-gray-300 dark:border-[#3d2c4f] bg-white dark:bg-[#0f0d12] px-4 py-2 text-sm text-gray-900 dark:text-gray-100 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                placeholder="seller@example.com&#10;another@example.com"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {emailsText.split(/[\n,]/).map((e) => e.trim()).filter(Boolean).length} email(s) entered
              </p>
            </div>
          ) : (
            <CustomTextInput
              label="Number of invites"
              type="number"
              value={String(count)}
              onChange={(e) => setCount(Math.max(1, Math.min(100, Number(e.target.value))))}
            />
          )}

          <CustomTextInput
            label="Expires in (days)"
            type="number"
            value={String(expiresInDays)}
            onChange={(e) => setExpiresInDays(Math.max(1, Math.min(365, Number(e.target.value))))}
          />

          <div className="flex gap-2 pt-2">
            <CustomButton onClick={handleCreate} variant="primary" loading={saving}>
              Create Invites
            </CustomButton>
            <CustomButton onClick={onClose} variant="secondary">
              Cancel
            </CustomButton>
          </div>
        </div>
      </div>
    </div>
  )
}
